<template>
	<ActionCard
		icon-shape="circle"
		header-class="gap-0.5!"
		:icon="event ? ICONS.pages.calendar : 'jm:calendar-empty'"
		:title="event ? $date(event.datetime, 'datetime') : $t(`appointments.common.empty_title`)"
		:embedded
	>
		<template #title>
			<div class="flex flex-col gap-0.5">
				<div v-if="!embedded" class="text-fs-xs text-primary font-medium">
					{{ $t(`patients.common.next_visit`) }}
				</div>
				<div v-if="event" class="text-sm">{{ $date(event.datetime, "date_long") }}</div>
				<div v-else class="text-tertiary font-normal" :class="{ 'max-w-50': embedded }">
					{{ $t(`patients.common.no_next_visit`) }}
				</div>
			</div>
		</template>
		<template v-if="event" #description>{{ eventTimeRange }}</template>
		<template #extra>
			<JMButton
				v-if="event"
				type="secondary"
				size="small"
				:label="$t(`appointments.common.edit`)"
				@click="openEditModal()"
			/>

			<AddAppointmentButton
				v-else
				type="primary"
				:patient-id
				icon-placement="right"
				size="small"
				:label="$t(`appointments.common.create_embedded`)"
				@saved="handleSaved()"
			/>

			<AppointmentFormModal
				v-model:show="showFormModal"
				:appointment="event"
				:show-appointment-type-tabs="false"
				patient-readonly
				@saved="handleSaved()"
				@deleted="handleSaved()"
			/>
		</template>
	</ActionCard>
</template>

<script setup lang="ts">
import type { Appointment } from "@/types/appointment"
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import AddAppointmentButton from "@/components/calendar/AddAppointmentButton.vue"
import AppointmentFormModal from "@/components/calendar/AppointmentFormModal.vue"
import ActionCard from "@/components/common/ActionCard.vue"
import JMButton from "@/components/common/JMButton.vue"
import { ICONS } from "@/const"
import dayjs, { dFormat } from "@/utils/dayjs"

const props = defineProps<{
	event?: Appointment | null
	patientId: number
	embedded?: boolean
}>()

const emit = defineEmits<{
	saved: []
}>()

const { t } = useI18n()

const eventTimeRange = computed(() => {
	const event = props.event
	if (!event) return ""

	if (event.all_day) {
		return t(`appointments.form.fields.all_day`)
	}

	const start = dayjs(event.datetime)
	const end = start.add(event.duration, "minute")

	return `${dFormat(start, "time")} - ${dFormat(end, "time")}`
})

const showFormModal = ref(false)

function openEditModal() {
	if (!props.event) return

	showFormModal.value = true
}

function handleSaved() {
	showFormModal.value = false
	emit("saved")
}
</script>
